/**
 * Role Profile API Service
 *
 * Role profiles held in Role Master: the versioned, approved description of each
 * role (purpose, accountability areas and deliverables with KPIs, the 4Sight
 * managerial / leadership framework, requirements). The approved version feeds
 * printed profiles, Neptune requisitions and the performance module.
 */

import { apiClient } from '../apiClient';

const BASE = '/identity/org/role-profiles';

// =============================================================================
// TYPES
// =============================================================================

export type ProfileStatus = 'draft' | 'submitted' | 'approved' | 'superseded';
export type ProfileItemKind = 'accountability' | 'deliverable' | 'framework';
export type ProfileSource = 'manual' | 'word' | 'excel' | 'copy';

export interface ProfileItem {
  perspective: string;
  kind: ProfileItemKind;
  framework_code: string | null;
  title: string | null;
  detail: string | null;
  kpi: string | null;
  applicable: boolean;
}

export interface ProfileContent {
  purpose: string | null;
  advert_summary: string | null;
  work_conditions: string | null;
  qualifications_minimum: string[];
  qualifications_advantageous: string[];
  experience_minimum: string[];
  experience_advantageous: string[];
  min_education_nqf: number | null;
  functional_competencies: string[];
  behavioural_competencies: string[];
  reviewed_at: string | null;
  items: ProfileItem[];
}

export interface ProfileCompleteness {
  score: number;
  missing: string[];
  neptune_ready: boolean;
  neptune_missing: string[];
}

export interface NeptuneFields {
  job_advert_summary: string | null;
  duties_and_responsibilities: string | null;
  minimum_requirements: string | null;
  advantageous_requirements: string | null;
  minimum_education_nqf: number | null;
  minimum_education_label: string | null;
  functional_competencies: string[];
  behavioural_competencies: string[];
}

export interface ProfileVersion {
  id: number;
  version: number;
  status: ProfileStatus;
  source: ProfileSource;
  source_filename: string | null;
  created_at: string | null;
  updated_at: string | null;
  submitted_at: string | null;
  submitted_by_name: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  approval_note: string | null;
  content: ProfileContent;
  completeness: ProfileCompleteness;
  neptune: NeptuneFields;
}

/** Live Role Master / position facts: shown with the profile, never stored in it. */
export interface RoleFacts {
  role_id: number;
  role_code: string;
  title: string;
  function: string | null;
  department: string | null;
  team_1: string | null;
  team_2: string | null;
  team_3: string | null;
  grade: string | null;
  occupational_level: string | null;
  is_critical_role: boolean;
  is_academic: boolean;
  status: string;
  seats: number;
  employees_in_role: number;
  vacant_seats: number;
  direct_reports: number;
  reports_to_roles: [string, number][];
  roles_reporting_in: [string, number][];
  campuses: [string, number][];
}

export interface ProfileHistoryEntry {
  id: number;
  version: number;
  status: ProfileStatus;
  source: ProfileSource;
  source_filename: string | null;
  updated_at: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
}

export interface RoleProfileBundle {
  role: RoleFacts;
  approved: ProfileVersion | null;
  open: ProfileVersion | null;
  history: ProfileHistoryEntry[];
  can_approve: boolean;
}

export interface FrameworkItemDef {
  code: string;
  perspective: string;
  title: string;
  default_kpi: string | null;
}

export interface ProfileFramework {
  perspectives: { code: string; label: string; subtitle?: string }[];
  kinds: { code: 'accountability' | 'deliverable'; label: string; help: string }[];
  framework_items: FrameworkItemDef[];
  nqf_levels: { level: number; label: string }[];
  default_behavioural_competencies: string[];
}

export type ProfileState = 'none' | 'draft' | 'submitted' | 'approved' | 'approved_with_draft';

export interface ProfileSummaryRow {
  role_id: number;
  role_code: string;
  title: string;
  function: string | null;
  department: string | null;
  grade: string | null;
  seats: number;
  filled: number;
  state: ProfileState;
  approved_version: number | null;
  approved_at: string | null;
  reviewed_at: string | null;
  review_due: boolean;
  open_version: number | null;
  open_status: ProfileStatus | null;
  open_source: ProfileSource | null;
  open_updated_at: string | null;
  completeness: number;
  neptune_ready: boolean;
}

export interface ProfileSummary {
  rows: ProfileSummaryRow[];
  totals: {
    roles: number;
    approved: number;
    in_progress: number;
    submitted: number;
    none: number;
    neptune_ready: number;
    review_due: number;
  };
}

export type ImportStatus = 'applied' | 'needs_role' | 'conflict' | 'failed' | 'discarded';

export interface ImportCandidate {
  role_id: number;
  role_code: string | null;
  title: string;
  department: string | null;
  score: number;
  department_matches: boolean;
}

export interface ProfileImport {
  id: number;
  batch_id: string;
  filename: string;
  file_format: string | null;
  sheet_name: string | null;
  status: ImportStatus;
  extracted_title: string | null;
  match_reason: string | null;
  candidates: ImportCandidate[];
  role_master_entry_id: number | null;
  profile_id: number | null;
  error: string | null;
  created_at: string | null;
  applied_at: string | null;
  warnings: string[];
  source: Record<string, string>;
  completeness: ProfileCompleteness | null;
}

export interface ProfileImportDetail extends ProfileImport {
  parsed: { content: ProfileContent; unmapped: { label: string; text: string }[] };
}

export interface UploadResult {
  batch_id: string;
  counts: Record<'applied' | 'needs_role' | 'conflict' | 'failed', number>;
  items: ProfileImport[];
}

// =============================================================================
// SERVICE
// =============================================================================

export const roleProfileService = {
  async getFramework(): Promise<ProfileFramework> {
    return (await apiClient.get<ProfileFramework>(`${BASE}/framework`)).data;
  },

  async getSummary(): Promise<ProfileSummary> {
    return (await apiClient.get<ProfileSummary>(`${BASE}/summary`)).data;
  },

  async getRole(roleId: number): Promise<RoleProfileBundle> {
    return (await apiClient.get<RoleProfileBundle>(`${BASE}/roles/${roleId}`)).data;
  },

  async startDraft(roleId: number, startFrom: 'approved' | 'blank' = 'approved'): Promise<RoleProfileBundle> {
    return (await apiClient.post<RoleProfileBundle>(`${BASE}/roles/${roleId}/draft`, { start_from: startFrom })).data;
  },

  async saveDraft(roleId: number, content: ProfileContent): Promise<RoleProfileBundle> {
    return (await apiClient.put<RoleProfileBundle>(`${BASE}/roles/${roleId}/draft`, content)).data;
  },

  async submitDraft(roleId: number): Promise<RoleProfileBundle> {
    return (await apiClient.post<RoleProfileBundle>(`${BASE}/roles/${roleId}/draft/submit`)).data;
  },

  async approveDraft(roleId: number, note?: string): Promise<RoleProfileBundle> {
    return (await apiClient.post<RoleProfileBundle>(`${BASE}/roles/${roleId}/draft/approve`, { note: note || null })).data;
  },

  async discardDraft(roleId: number): Promise<RoleProfileBundle> {
    return (await apiClient.delete<RoleProfileBundle>(`${BASE}/roles/${roleId}/draft`)).data;
  },

  async copyFrom(roleId: number, sourceRoleId: number): Promise<RoleProfileBundle> {
    return (await apiClient.post<RoleProfileBundle>(`${BASE}/roles/${roleId}/copy-from/${sourceRoleId}`)).data;
  },

  async exportDocx(roleId: number, version?: number): Promise<Blob> {
    const params = version ? `?version=${version}` : '';
    return (await apiClient.get<Blob>(`${BASE}/roles/${roleId}/export.docx${params}`, { responseType: 'blob' })).data;
  },

  async getNeptuneFields(roleId: number): Promise<NeptuneFields & { profile_version: number }> {
    return (await apiClient.get<NeptuneFields & { profile_version: number }>(`${BASE}/roles/${roleId}/neptune`)).data;
  },

  /** Upload up to 25 files per call; pass the same batchId to group several calls. */
  async uploadProfiles(files: File[], batchId?: string): Promise<UploadResult> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    if (batchId) form.append('batch_id', batchId);
    return (await apiClient.post<UploadResult>(`${BASE}/imports`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    })).data;
  },

  async listImports(statuses?: ImportStatus[], batchId?: string): Promise<{ items: ProfileImport[] }> {
    const params = new URLSearchParams();
    if (statuses?.length) params.set('status', statuses.join(','));
    if (batchId) params.set('batch_id', batchId);
    const qs = params.toString();
    return (await apiClient.get<{ items: ProfileImport[] }>(`${BASE}/imports${qs ? `?${qs}` : ''}`)).data;
  },

  async getImport(id: number): Promise<ProfileImportDetail> {
    return (await apiClient.get<ProfileImportDetail>(`${BASE}/imports/${id}`)).data;
  },

  async applyImport(id: number, roleId: number, replaceOpenDraft = false): Promise<ProfileImport> {
    return (await apiClient.post<ProfileImport>(`${BASE}/imports/${id}/apply`, {
      role_master_entry_id: roleId,
      replace_open_draft: replaceOpenDraft,
    })).data;
  },

  async discardImport(id: number): Promise<ProfileImport> {
    return (await apiClient.post<ProfileImport>(`${BASE}/imports/${id}/discard`)).data;
  },
};

export default roleProfileService;
